import React, { useContext } from "react";
import { CoverageService } from "@services";
import EditNote from "@mui/icons-material/EditNote";
import { CommonService, PendoService } from "@services";
import { ConfigurationContext } from "../../../contexts/Configuration";
import { SearchContext } from "../../../contexts/Search";
import DynamicHtml from "../CommonComponent/DynamicHtml";

const DisplayPrescriberNotes = ({
  row,
  dynamicColumns,
  prescriberCustomNotes,
  stateId,
  cellIcon,
  setOpen,
  setPrescriberNoteResponse,
  setAdditionalPrescriberNoteCriteria,
  setContingentDataField,
  setActiveRow,
  setActivePlanName,
  setIsLoadingPage,
  setCustomNoteData,
  setCustomNotePopupOpen,
  captureScrollPositions,
  showDataSource,
}) => {
  const { appliedConfiguration } = useContext(ConfigurationContext);
  const { benefitType, productId, productName, indicationName } = useContext(SearchContext);

  const filterPrescriberCustomNotesByBenefitType = () => {
    if (!prescriberCustomNotes || !prescriberCustomNotes.length) {
      return null;
    }
    const benefitTypeId = benefitType === "Medical" ? 1 : benefitType === "Pharmacy" ? 2 : 3;
    return prescriberCustomNotes.filter((note) => note.benefitTypeId === benefitTypeId);
  };

  const shouldShowCustomNotePopup = (filteredPrescriberCustomNotes, currentRow) => {
    if (!filteredPrescriberCustomNotes || !filteredPrescriberCustomNotes.length) {
      return false;
    }

    let statusDataField = dynamicColumns?.find((x) => x.isStatusColumn)?.dataField;
    if (!statusDataField) {
      statusDataField = "Status";
    }

    function checkCustomNoteApplies(customNote) {
      const noteStatuses = customNote.coverageStatuses?.split(",");

      if (
        (noteStatuses?.includes("Not Covered") && currentRow[statusDataField] == "Not Covered") ||
        (noteStatuses?.includes("Unknown") && currentRow[statusDataField] == "Unknown")
      ) {
        return true;
      } else if (noteStatuses?.includes("Covered")) {
        return currentRow.PA?.trim()?.toLowerCase() == "no" && currentRow.ST?.trim()?.toLowerCase() == "no";
      } else {
        return false;
      }
    }

    const currentRowCustomNoteData = filteredPrescriberCustomNotes.filter(checkCustomNoteApplies);
    let showCustomNotePopup = currentRowCustomNoteData && currentRowCustomNoteData?.length > 0;
    if (showCustomNotePopup) {
      setCustomNoteData(currentRowCustomNoteData[0]);
    }

    return showCustomNotePopup;
  };
  const showPNCustomNotePopup = (data, property) => async () => {
    captureScrollPositions();
    //pendo track event
    PendoService.getInstance().trackEvent(
      data?.customLink,
      "showCustomNotePopup",
      property.PlanName || property.ControllerName,
      productId,
      productName,
      indicationName
    );
    setIsLoadingPage(true);
    setActivePlanName(property.PlanName || property.ControllerName);
    setActiveRow(property);
    setOpen(false);
    setCustomNoteData({ noteDetails: data?.customNote });
    setCustomNotePopupOpen(true);
    setIsLoadingPage(false);
  }
  const showNotePopup = (property) => async () => {
    captureScrollPositions();
    //pendo track event
    PendoService.getInstance().trackEvent(
      "ShowNote click",
      "showNotePopup",
      property.PlanName || property.ControllerName,
      productId,
      productName,
      indicationName
    );

    setIsLoadingPage(true);
    const filteredPrescriberCustomNotes = filterPrescriberCustomNotesByBenefitType();
    const shouldShowNotePopup = shouldShowCustomNotePopup(filteredPrescriberCustomNotes, property);

    const benefitTypeId = benefitType === "Medical" ? 1 : benefitType === "Pharmacy" ? 2 : 3;
    const coverageFinderPrescriberNotesFields = appliedConfiguration.coverageFinderPrescriberNotes.filter(
      (obj) => obj.benefitTypeId === benefitTypeId
    );

    const makePrescriberNoteRequest = coverageFinderPrescriberNotesFields.some(
      (field) => !CommonService.objectHasProperty(row, field.dataField)
    );

    setActivePlanName(property.PlanName || property.ControllerName);
    setActiveRow(property);
    if (shouldShowNotePopup) {
      setIsLoadingPage(false);
      setOpen(false);
      setCustomNotePopupOpen(true);
    } else {
      setCustomNotePopupOpen(false);

      if (makePrescriberNoteRequest) {
        const prescriberNoteRequest = {
          ProductId: appliedConfiguration.productId,
          IndicationId: appliedConfiguration.indicationId,
          FormularyId: property.FormularyID || property.majority_FormularyID,
          IsMedical: benefitType != "Pharmacy",
          ProjectId: appliedConfiguration.projectId,
          PlanId: property.PlanId,
          ControllerId: property.ControllerId,
          Channel: property.Channel,
          StateId: stateId,
          ResultName:
            benefitType != "Pharmacy"
              ? appliedConfiguration.coverageFinderConfiguration.resultName
              : appliedConfiguration.coverageFinderConfiguration.pharmacyResultName,
        };
        const prescriberNoteResponse = await CoverageService.getInstance().getPrescriberNotes(prescriberNoteRequest);

        if (prescriberNoteResponse != null) {
          updatePAFormLinks(prescriberNoteResponse.prescriberNoteCriteria);
          setPrescriberNoteResponse([prescriberNoteResponse.prescriberNoteCriteria]);
          setAdditionalPrescriberNoteCriteria([prescriberNoteResponse.additionalPrescriberNoteCriteria]);
        }
      }
      else {
        setPrescriberNoteResponse(null);
        setAdditionalPrescriberNoteCriteria(null);
      }

      setOpen(true);
      const coverageContingentField = dynamicColumns?.find((x) => x.coverageContingentData);
      setContingentDataField(coverageContingentField?.dataField);
      setIsLoadingPage(false);
    }
  };

  const updatePAFormLinks = (prescriberNoteResponse) => {
    const PAFormLinkFieldName = "PA_Form_Links";
    if (prescriberNoteResponse[PAFormLinkFieldName] && prescriberNoteResponse[PAFormLinkFieldName] !== "")
      prescriberNoteResponse[PAFormLinkFieldName] = addTargetBlankToUrls(prescriberNoteResponse[PAFormLinkFieldName]);
  };
  function isJsonString(value) {
    if (typeof value !== "string") return false;
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object";
    } catch (e) {
      return false;
    }
  }
  function addTargetBlankToUrls(htmlString) {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlString;

    const links = tempDiv.querySelectorAll("a");
    links.forEach((link) => {
      link.setAttribute("target", "_blank");
      link.setAttribute("rel", "noopener noreferrer");
    });
    return tempDiv.innerHTML;
  }
  function prescriberNoteCustomLink(value, row) {
    const data = JSON.parse(value);
    return (
      <button className="norstella-cf-note-button" part="cfNoteButton" onClick={showPNCustomNotePopup(data, row)}>
        {data.iconPath &&
          <img
            part="cfNoteRowCellIcon"
            className="norstella-cf-DynamicColCellIcon"
            src={data?.iconPath}
            alt={data?.altText ?? "Icon"} />}
        {data.customLink && (
          <span part="cfNoteRowText" className="norstella-cf-tableRowCellNoteText">
            {data.customLink}
          </span>
        )}
      </button>
    );
  }

  return (
    <>
      {row["PrescriberNotes_DisplayValue"] !== null &&
        row["PrescriberNotes_DisplayValue"] !== undefined &&
        row["PrescriberNotes_DisplayValue"] !== "display icon" ?
        (isJsonString(row["PrescriberNotes_DisplayValue"]) ? prescriberNoteCustomLink(row["PrescriberNotes_DisplayValue"], row) :
          <DynamicHtml
            input={row["PrescriberNotes_DisplayValue"]}
            className={showDataSource ? "norstella-cf-dataSource" : "norstella-cf-customValue"}
            ignorePlaceholders={true}
          />
        ) : (
          <button className="norstella-cf-note-button" part="cfNoteButton" onClick={showNotePopup(row)}>
            {cellIcon ? (
              <img
                part="cfNoteRowCellIcon"
                className="norstella-cf-DynamicColCellIcon"
                src={cellIcon?.iconPath}
                alt={cellIcon?.altText ?? "Icon"}
              />
            ) : (
              <EditNote part="cfNoteRowCellIcon" className="norstella-cf-tableRowCellIcon"></EditNote>
            )}
            <span part="cfNoteRowText" className="norstella-cf-tableRowCellNoteText">
              Show Details
            </span>
          </button>
        )}
    </>
  );
};

export default DisplayPrescriberNotes;
